import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useTenant } from '../context/TenantContext';
import '../styles/KioskManagement.css';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import TabletIcon from '@mui/icons-material/Tablet';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import logger from '../utils/logger';
import { previewStation } from '../utils/stationName';

/**
 * Area and unit number for a station, with a preview of the code and label the
 * server will assign. A station is named after the desk it sits at rather than
 * the tablet on it, so replacing hardware never changes what staff call it.
 */
const StationFields = ({ form, setForm, autoFocus }) => {
  const preview = previewStation(form.area, form.unitNo);

  return (
    <>
      <div className="kiosk-form-group">
        <label>Area</label>
        <input
          type="text"
          value={form.area}
          onChange={(e) => setForm(prev => ({ ...prev, area: e.target.value }))}
          placeholder="e.g. Front Desk, Front Desk North, Room 3"
          className="kiosk-form-input"
          autoFocus={autoFocus}
        />
      </div>

      <div className="kiosk-form-group">
        <label>Number (optional)</label>
        <input
          type="number"
          min="1"
          max="99"
          value={form.unitNo}
          onChange={(e) => setForm(prev => ({ ...prev, unitNo: e.target.value }))}
          placeholder="Leave blank for the next free number"
          className="kiosk-form-input"
        />
      </div>

      {preview && (
        <div className="kiosk-station-preview">
          <span className="kiosk-station-preview-code">{preview.code}</span>
          <span className="kiosk-station-preview-label">{preview.label}</span>
          {preview.autoNumbered && (
            <span className="kiosk-station-preview-note">number assigned on save</span>
          )}
        </div>
      )}

      <div className="kiosk-form-group">
        <label>Note (optional)</label>
        <input
          type="text"
          value={form.note}
          onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
          placeholder="e.g. iPad 9th gen, black case"
          className="kiosk-form-input"
        />
      </div>
    </>
  );
};

const KioskManagement = () => {
  const { tenant } = useTenant();
  const [activeTab, setActiveTab] = useState(0);

  // Admins state
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [showDeleteAdminModal, setShowDeleteAdminModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [adminToEdit, setAdminToEdit] = useState(null);

  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '', deviceLabel: '' });
  const [adminError, setAdminError] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);

  // Patients (devices) state
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [showCreateDeviceModal, setShowCreateDeviceModal] = useState(false);
  const [showDeleteDeviceModal, setShowDeleteDeviceModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  const [deviceForm, setDeviceForm] = useState({ area: '', unitNo: '', note: '' });
  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [deviceSaving, setDeviceSaving] = useState(false);

  // QR Code state
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      setAdminsLoading(true);
      const params = tenant?.id ? `?tenantId=${tenant.id}` : '';
      const res = await api.get(`/api/kiosk/admin/list${params}`);
      setAdmins(res.data.admins || []);
    } catch (err) {
      logger.error('Error fetching kiosk admins:', err);
    } finally {
      setAdminsLoading(false);
    }
  }, [tenant?.id]);

  const fetchDevices = useCallback(async () => {
    try {
      setDevicesLoading(true);
      const params = tenant?.id ? `?tenantId=${tenant.id}` : '';
      const res = await api.get(`/api/kiosk/manage/list${params}`);
      setDevices(res.data.devices || []);
    } catch (err) {
      logger.error('Error fetching kiosk devices:', err);
    } finally {
      setDevicesLoading(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    fetchAdmins();
    fetchDevices();
  }, [fetchAdmins, fetchDevices]);

  useEffect(() => {
    if (activeTab !== 1) return;
    const interval = setInterval(fetchDevices, 15000);
    return () => clearInterval(interval);
  }, [activeTab, fetchDevices]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // --- Admin handlers ---
  const openCreateAdmin = () => {
    setAdminForm({ name: '', email: '', password: '', deviceLabel: '' });
    setAdminError('');
    setShowCreateAdminModal(true);
  };

  const handleCreateAdmin = async () => {
    const { name, email, password } = adminForm;
    if (!name.trim() || !email || !password) {
      setAdminError('Name, email, and password are required');
      return;
    }
    if (password.length < 6) {
      setAdminError('Password must be at least 6 characters');
      return;
    }
    if (!tenant?.id) {
      setAdminError('No tenant selected');
      return;
    }
    try {
      setAdminSaving(true);
      setAdminError('');
      await api.post('/api/kiosk/admin/create', {
        name: name.trim(),
        email,
        password,
        tenantId: tenant.id,
        deviceLabel: adminForm.deviceLabel.trim() || undefined,
      });
      setShowCreateAdminModal(false);
      fetchAdmins();
    } catch (err) {
      setAdminError(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setAdminSaving(false);
    }
  };

  const openEditAdmin = (admin) => {
    setAdminToEdit(admin);
    setAdminForm({ name: admin.name, email: admin.email, password: '', deviceLabel: admin.device_label || '' });
    setAdminError('');
    setShowEditAdminModal(true);
  };

  const handleEditAdmin = async () => {
    if (!adminForm.name.trim() || !adminForm.email) {
      setAdminError('Name and email are required');
      return;
    }
    try {
      setAdminSaving(true);
      setAdminError('');
      const body = {
        name: adminForm.name.trim(),
        email: adminForm.email,
        deviceLabel: adminForm.deviceLabel.trim() || null,
      };
      if (adminForm.password) body.password = adminForm.password;
      await api.put(`/api/kiosk/admin/${adminToEdit.id}`, body);
      setShowEditAdminModal(false);
      fetchAdmins();
    } catch (err) {
      setAdminError(err.response?.data?.message || 'Failed to update admin');
    } finally {
      setAdminSaving(false);
    }
  };

  const confirmDeleteAdmin = async () => {
    if (!adminToDelete) return;
    try {
      await api.delete(`/api/kiosk/admin/${adminToDelete.id}`);
      setShowDeleteAdminModal(false);
      setAdminToDelete(null);
      fetchAdmins();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete admin');
    }
  };

  // --- Device handlers ---
  const openCreateDevice = () => {
    setDeviceForm({ area: '', unitNo: '', note: '' });
    setDeviceError('');
    setShowCreateDeviceModal(true);
  };

  const openEditDevice = (device) => {
    setDeviceToEdit(device);
    setDeviceForm({
      area: device.area || device.label || '',
      unitNo: device.unit_no ? String(device.unit_no) : '',
      note: device.note || '',
    });
    setDeviceError('');
    setShowEditDeviceModal(true);
  };

  const handleCreateDevice = async () => {
    const { area, unitNo, note } = deviceForm;
    if (!area.trim()) {
      setDeviceError('Area is required — for example "Front Desk" or "Room 3"');
      return;
    }
    if (!tenant?.id) {
      setDeviceError('No tenant selected');
      return;
    }
    try {
      setDeviceSaving(true);
      setDeviceError('');
      const res = await api.post('/api/kiosk/manage/create', {
        area: area.trim(),
        unitNo: unitNo === '' ? undefined : Number(unitNo),
        note: note.trim() || undefined,
        tenantId: tenant.id,
      });
      setShowCreateDeviceModal(false);
      fetchDevices();
      if (res.data.device?.id) {
        handleShowQr(res.data.device);
      }
    } catch (err) {
      setDeviceError(err.response?.data?.message || 'Failed to create device');
    } finally {
      setDeviceSaving(false);
    }
  };

  const handleEditDevice = async () => {
    const { area, unitNo, note } = deviceForm;
    if (!area.trim()) {
      setDeviceError('Area is required — for example "Front Desk" or "Room 3"');
      return;
    }
    try {
      setDeviceSaving(true);
      setDeviceError('');
      await api.put(`/api/kiosk/manage/${deviceToEdit.id}`, {
        area: area.trim(),
        unitNo: unitNo === '' ? null : Number(unitNo),
        note: note.trim() || null,
      });
      setShowEditDeviceModal(false);
      setDeviceToEdit(null);
      fetchDevices();
    } catch (err) {
      setDeviceError(err.response?.data?.message || 'Failed to rename station');
    } finally {
      setDeviceSaving(false);
    }
  };

  const confirmDeleteDevice = async () => {
    if (!deviceToDelete) return;
    try {
      await api.delete(`/api/kiosk/manage/${deviceToDelete.id}`);
      setShowDeleteDeviceModal(false);
      setDeviceToDelete(null);
      fetchDevices();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete device');
    }
  };

  const handleShowQr = async (device) => {
    setShowQrModal(true);
    setQrLoading(true);
    setQrData(null);
    try {
      const res = await api.get(`/api/kiosk/manage/${device.id}/qr`);
      setQrData(res.data);
    } catch (err) {
      logger.error('Error fetching QR code:', err);
      setQrData({ error: err.response?.data?.message || 'Failed to generate QR code' });
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQr = async () => {
    if (!qrData?.qrCode) return;

    const QR = 300, BH = 42, BW = 2, BR = 14;
    const W = BW * 2 + QR;
    const H = BW + QR + BH + BW;

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.roundRect(0, 0, W, H, BR);
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(0, 0, W, H, BR);
    ctx.clip();

    const qrImg = new Image();
    await new Promise((r) => { qrImg.onload = r; qrImg.src = qrData.qrCode; });
    ctx.drawImage(qrImg, BW, BW, QR, QR);

    const logoSrc = qrData.logoUrl || tenant?.logoUrl;
    if (logoSrc) {
      try {
        const logoImg = new Image();
        await new Promise((r, j) => { logoImg.onload = r; logoImg.onerror = j; logoImg.src = logoSrc; });
        const ls = QR * 0.22;
        const lx = BW + (QR - ls) / 2;
        const ly = BW + (QR - ls) / 2;
        ctx.drawImage(logoImg, lx, ly, ls, ls);
      } catch {}
    }

    ctx.fillStyle = '#0F72B5';
    ctx.fillRect(BW, BW + QR, W - BW * 2, BH);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 16px -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(qrData.stationCode || qrData.label || 'Kiosk', W / 2, BW + QR + BH / 2);

    ctx.restore();

    ctx.strokeStyle = '#0F72B5';
    ctx.lineWidth = BW;
    ctx.beginPath();
    ctx.roundRect(BW / 2, BW / 2, W - BW, H - BW, BR);
    ctx.stroke();

    const link = document.createElement('a');
    link.download = `station-qr-${qrData.stationCode || qrData.label || 'device'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleRegenerateQr = async (deviceId) => {
    setQrLoading(true);
    try {
      await api.post(`/api/kiosk/manage/${deviceId}/regenerate-qr`);
      const res = await api.get(`/api/kiosk/manage/${deviceId}/qr`);
      setQrData(res.data);
      fetchDevices();
    } catch (err) {
      logger.error('Error regenerating QR:', err);
      setQrData({ error: 'Failed to regenerate QR code' });
    } finally {
      setQrLoading(false);
    }
  };

  return (
    <div className="kiosk-mgmt-wrapper">
      <div className="kiosk-mgmt-header">
        <div className="kiosk-mgmt-title-row">
          <TabletIcon style={{ fontSize: 28, color: 'var(--color-primary, #06ab8b)' }} />
          <div>
            <h2 className="kiosk-mgmt-title">Kiosk Management</h2>
            <p className="kiosk-mgmt-subtitle">
              Manage kiosk admin users and devices
            </p>
          </div>
        </div>
        {activeTab === 0 ? (
          <button className="kiosk-create-btn" onClick={openCreateAdmin}>
            <AddIcon style={{ fontSize: 20 }} />
            Create Admin
          </button>
        ) : (
          <button className="kiosk-create-btn" onClick={openCreateDevice}>
            <AddIcon style={{ fontSize: 20 }} />
            Add Station
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="kiosk-tabs">
        <button
          className={`kiosk-tab ${activeTab === 0 ? 'active' : ''}`}
          onClick={() => setActiveTab(0)}
        >
          <AdminPanelSettingsIcon style={{ fontSize: 18 }} />
          Kiosk Admins
          <span className="kiosk-tab-count">{admins.length}</span>
        </button>
        <button
          className={`kiosk-tab ${activeTab === 1 ? 'active' : ''}`}
          onClick={() => setActiveTab(1)}
        >
          <PersonIcon style={{ fontSize: 18 }} />
          Kiosk Stations
          <span className="kiosk-tab-count">{devices.length}</span>
        </button>
      </div>

      {/* Admins Tab */}
      {activeTab === 0 && (
        <div className="kiosk-table-container">
          <table className="kiosk-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Device</th>
                <th>Last Login</th>
                <th>Created</th>
                <th className="kiosk-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {adminsLoading ? (
                <tr><td colSpan="6" className="kiosk-empty-cell">Loading admins...</td></tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan="6" className="kiosk-empty-cell">
                    <div className="kiosk-empty-state">
                      <AdminPanelSettingsIcon style={{ fontSize: 48, color: '#ccc' }} />
                      <p>No kiosk admins created yet</p>
                      <button className="kiosk-create-btn-sm" onClick={openCreateAdmin}>Create First Admin</button>
                    </div>
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', background: '#e0f2f1',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#00897b', fontWeight: 600, fontSize: 14,
                        }}>
                          {admin.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <span className="kiosk-pin-label">{admin.name}</span>
                      </div>
                    </td>
                    <td style={{ color: '#555', fontSize: 14 }}>{admin.email}</td>
                    <td style={{ color: '#888', fontSize: 14 }}>{admin.device_label || '—'}</td>
                    <td style={{ color: '#888', fontSize: 14 }}>
                      {admin.last_login ? formatDate(admin.last_login) : 'Never'}
                    </td>
                    <td className="kiosk-date-cell">{formatDate(admin.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="kiosk-delete-btn"
                          onClick={() => openEditAdmin(admin)}
                          title="Edit Admin"
                          style={{ background: '#f0f7ff', color: '#2563eb' }}
                        >
                          <EditIcon style={{ fontSize: 18 }} />
                        </button>
                        <button
                          className="kiosk-delete-btn"
                          onClick={() => { setAdminToDelete(admin); setShowDeleteAdminModal(true); }}
                          title="Delete Admin"
                        >
                          <DeleteIcon style={{ fontSize: 18 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Devices Tab */}
      {activeTab === 1 && (
        <div className="kiosk-table-container">
          <table className="kiosk-table">
            <thead>
              <tr>
                <th>Station</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th className="kiosk-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {devicesLoading ? (
                <tr><td colSpan="5" className="kiosk-empty-cell">Loading devices...</td></tr>
              ) : devices.length === 0 ? (
                <tr>
                  <td colSpan="5" className="kiosk-empty-cell">
                    <div className="kiosk-empty-state">
                      <TabletIcon style={{ fontSize: 48, color: '#ccc' }} />
                      <p>No kiosk stations yet</p>
                      <button className="kiosk-create-btn-sm" onClick={openCreateDevice}>Add First Station</button>
                    </div>
                  </td>
                </tr>
              ) : (
                devices.map((device) => (
                  <tr key={device.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          minWidth: 44, height: 36, padding: '0 6px', borderRadius: 8, background: '#f0faf7',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--color-primary, #06ab8b)', flexShrink: 0,
                          fontFamily: 'monospace', fontSize: 12, fontWeight: 700, letterSpacing: '0.03em',
                        }}>
                          {/* Stations created before station naming have no code
                              and keep the tablet icon. */}
                          {device.station_code
                            ? device.station_code.split('-')[0]
                            : <TabletIcon style={{ fontSize: 20 }} />}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>{device.label}</div>
                          <div style={{ fontSize: 12, color: '#888', fontFamily: device.station_code ? 'monospace' : undefined }}>
                            {device.station_code || device.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`kiosk-status-text ${device.is_active ? 'active' : 'inactive'}`}>
                        {device.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {device.last_login_at ? (
                        <div>
                          <div style={{ fontSize: 13, color: '#555' }}>{formatDateTime(device.last_login_at)}</div>
                          {device.last_login_ip && (
                            <div style={{ fontSize: 11, color: '#aaa', fontFamily: 'monospace' }}>
                              IP: {device.last_login_ip}
                            </div>
                          )}
                          {device.last_login_device && (
                            <div style={{ fontSize: 11, color: '#aaa' }}>
                              {device.last_login_device.split('—')[0]?.trim()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#ccc', fontSize: 13 }}>Never</span>
                      )}
                    </td>
                    <td className="kiosk-date-cell">{formatDate(device.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="kiosk-delete-btn"
                          onClick={() => handleShowQr(device)}
                          title="Download QR Code"
                          style={{ background: '#f0faf7', color: 'var(--color-primary, #06ab8b)' }}
                        >
                          <QrCode2Icon style={{ fontSize: 18 }} />
                        </button>
                        <button
                          className="kiosk-delete-btn"
                          onClick={() => openEditDevice(device)}
                          title="Rename Station"
                          style={{ background: '#f0f7ff', color: '#2563eb' }}
                        >
                          <EditIcon style={{ fontSize: 18 }} />
                        </button>
                        <button
                          className="kiosk-delete-btn"
                          onClick={() => handleRegenerateQr(device.id)}
                          title="Regenerate QR Code"
                          style={{ background: '#fffbeb', color: '#d97706' }}
                        >
                          <RefreshIcon style={{ fontSize: 18 }} />
                        </button>
                        <button
                          className="kiosk-delete-btn"
                          onClick={() => { setDeviceToDelete(device); setShowDeleteDeviceModal(true); }}
                          title="Delete Device"
                        >
                          <DeleteIcon style={{ fontSize: 18 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateAdminModal && (
        <div className="kiosk-modal-overlay" onClick={() => setShowCreateAdminModal(false)}>
          <div className="kiosk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kiosk-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AddIcon style={{ color: 'white' }} />
                <h2>Create Kiosk Admin</h2>
              </div>
              <button className="kiosk-modal-close" onClick={() => setShowCreateAdminModal(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="kiosk-modal-body">
              <div className="kiosk-form-group">
                <label>Name</label>
                <input type="text" value={adminForm.name}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Front Desk Staff" className="kiosk-form-input" />
              </div>
              <div className="kiosk-form-group">
                <label>Email</label>
                <input type="email" value={adminForm.email}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="e.g. frontdesk@clinic.com" className="kiosk-form-input" />
              </div>
              <div className="kiosk-form-group">
                <label>Password</label>
                <input type="password" value={adminForm.password}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimum 6 characters" className="kiosk-form-input" />
              </div>
              <div className="kiosk-form-group">
                <label>Device Label (optional)</label>
                <input type="text" value={adminForm.deviceLabel}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, deviceLabel: e.target.value }))}
                  placeholder="e.g. Room 2 Tablet" className="kiosk-form-input" />
              </div>
              {adminError && <div className="kiosk-error-msg">{adminError}</div>}
              <div className="kiosk-modal-actions">
                <button className="kiosk-btn-cancel" onClick={() => setShowCreateAdminModal(false)}>Cancel</button>
                <button className="kiosk-btn-create" onClick={handleCreateAdmin} disabled={adminSaving}>
                  {adminSaving ? 'Creating...' : 'Create Admin'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditAdminModal && (
        <div className="kiosk-modal-overlay" onClick={() => setShowEditAdminModal(false)}>
          <div className="kiosk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kiosk-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <EditIcon style={{ color: 'white' }} />
                <h2>Edit Kiosk Admin</h2>
              </div>
              <button className="kiosk-modal-close" onClick={() => setShowEditAdminModal(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="kiosk-modal-body">
              <div className="kiosk-form-group">
                <label>Name</label>
                <input type="text" value={adminForm.name}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, name: e.target.value }))}
                  className="kiosk-form-input" />
              </div>
              <div className="kiosk-form-group">
                <label>Email</label>
                <input type="email" value={adminForm.email}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, email: e.target.value }))}
                  className="kiosk-form-input" />
              </div>
              <div className="kiosk-form-group">
                <label>New Password (leave blank to keep current)</label>
                <input type="password" value={adminForm.password}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Leave blank to keep current" className="kiosk-form-input" />
              </div>
              <div className="kiosk-form-group">
                <label>Device Label</label>
                <input type="text" value={adminForm.deviceLabel}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, deviceLabel: e.target.value }))}
                  placeholder="e.g. Room 2 Tablet" className="kiosk-form-input" />
              </div>
              {adminError && <div className="kiosk-error-msg">{adminError}</div>}
              <div className="kiosk-modal-actions">
                <button className="kiosk-btn-cancel" onClick={() => setShowEditAdminModal(false)}>Cancel</button>
                <button className="kiosk-btn-create" onClick={handleEditAdmin} disabled={adminSaving}>
                  {adminSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Admin Modal */}
      {showDeleteAdminModal && (
        <div className="kiosk-modal-overlay" onClick={() => setShowDeleteAdminModal(false)}>
          <div className="kiosk-modal kiosk-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="kiosk-modal-header kiosk-modal-header-danger">
              <h2>Delete Kiosk Admin</h2>
              <button className="kiosk-modal-close" onClick={() => setShowDeleteAdminModal(false)}><CloseIcon /></button>
            </div>
            <div className="kiosk-modal-body">
              <p style={{ margin: '0 0 8px', color: '#555' }}>Are you sure you want to delete this admin?</p>
              <div className="kiosk-delete-info">
                <strong>{adminToDelete?.name}</strong> — {adminToDelete?.email}
              </div>
              <p className="kiosk-delete-warning">This action cannot be undone.</p>
              <div className="kiosk-modal-actions">
                <button className="kiosk-btn-cancel" onClick={() => setShowDeleteAdminModal(false)}>Cancel</button>
                <button className="kiosk-btn-delete" onClick={confirmDeleteAdmin}>Delete Admin</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Device Modal */}
      {showCreateDeviceModal && (
        <div className="kiosk-modal-overlay" onClick={() => setShowCreateDeviceModal(false)}>
          <div className="kiosk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kiosk-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AddIcon style={{ color: 'white' }} />
                <h2>Add Station</h2>
              </div>
              <button className="kiosk-modal-close" onClick={() => setShowCreateDeviceModal(false)}><CloseIcon /></button>
            </div>
            <div className="kiosk-modal-body">
              <StationFields form={deviceForm} setForm={setDeviceForm} autoFocus />
              <p style={{ fontSize: 13, color: '#888', margin: '0 0 16px', lineHeight: 1.5 }}>
                A QR code is generated automatically. Print it and stick it on the tablet — staff can
                also pull it up any time from the Stations page in the kiosk admin.
              </p>
              {deviceError && <div className="kiosk-error-msg">{deviceError}</div>}
              <div className="kiosk-modal-actions">
                <button className="kiosk-btn-cancel" onClick={() => setShowCreateDeviceModal(false)}>Cancel</button>
                <button className="kiosk-btn-create" onClick={handleCreateDevice} disabled={deviceSaving}>
                  {deviceSaving ? 'Creating...' : 'Create Station'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Device Modal */}
      {showEditDeviceModal && (
        <div className="kiosk-modal-overlay" onClick={() => setShowEditDeviceModal(false)}>
          <div className="kiosk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kiosk-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <EditIcon style={{ color: 'white' }} />
                <h2>Rename Station</h2>
              </div>
              <button className="kiosk-modal-close" onClick={() => setShowEditDeviceModal(false)}><CloseIcon /></button>
            </div>
            <div className="kiosk-modal-body">
              <StationFields form={deviceForm} setForm={setDeviceForm} autoFocus />
              <p style={{ fontSize: 13, color: '#888', margin: '0 0 16px', lineHeight: 1.5 }}>
                Renaming keeps the same QR code, so the sticker already on this tablet stays valid.
              </p>
              {deviceError && <div className="kiosk-error-msg">{deviceError}</div>}
              <div className="kiosk-modal-actions">
                <button className="kiosk-btn-cancel" onClick={() => setShowEditDeviceModal(false)}>Cancel</button>
                <button className="kiosk-btn-create" onClick={handleEditDevice} disabled={deviceSaving}>
                  {deviceSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Device Modal */}
      {showDeleteDeviceModal && (
        <div className="kiosk-modal-overlay" onClick={() => setShowDeleteDeviceModal(false)}>
          <div className="kiosk-modal kiosk-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="kiosk-modal-header kiosk-modal-header-danger">
              <h2>Delete Device</h2>
              <button className="kiosk-modal-close" onClick={() => setShowDeleteDeviceModal(false)}><CloseIcon /></button>
            </div>
            <div className="kiosk-modal-body">
              <p style={{ margin: '0 0 8px', color: '#555' }}>Are you sure you want to delete this device?</p>
              <div className="kiosk-delete-info">
                <strong>{deviceToDelete?.label}</strong> — {deviceToDelete?.email}
              </div>
              <p className="kiosk-delete-warning">This action cannot be undone.</p>
              <div className="kiosk-modal-actions">
                <button className="kiosk-btn-cancel" onClick={() => setShowDeleteDeviceModal(false)}>Cancel</button>
                <button className="kiosk-btn-delete" onClick={confirmDeleteDevice}>Delete Device</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQrModal && (
        <div className="kiosk-modal-overlay" onClick={() => setShowQrModal(false)}>
          <div className="kiosk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kiosk-modal-header" style={{ background: 'var(--color-primary, #06ab8b)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <QrCode2Icon style={{ color: 'white' }} />
                <h2>Device QR Code</h2>
              </div>
              <button className="kiosk-modal-close" onClick={() => setShowQrModal(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="kiosk-modal-body" style={{ textAlign: 'center' }}>
              {qrLoading ? (
                <div style={{ padding: '40px 0' }}>
                  <p style={{ color: '#888' }}>Generating QR Code...</p>
                </div>
              ) : qrData?.error ? (
                <div style={{ padding: '20px 0' }}>
                  <p style={{ color: '#ef4444' }}>{qrData.error}</p>
                </div>
              ) : qrData ? (
                <>
                  <p style={{ fontSize: 14, color: '#555', marginBottom: 4 }}>
                    <strong>{qrData.label}</strong>
                  </p>
                  <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
                    Scan this QR code on the kiosk device to auto-login
                  </p>
                  <div style={{
                    display: 'inline-block',
                    border: '2px solid #0F72B5',
                    borderRadius: 14,
                    overflow: 'hidden',
                    background: '#fff',
                  }}>
                    <div style={{ position: 'relative' }}>
                      <img src={qrData.qrCode} alt="QR Code" style={{ width: 260, height: 260, display: 'block' }} />
                      {(qrData.logoUrl || tenant?.logoUrl) && (
                        <img
                          src={qrData.logoUrl || tenant?.logoUrl}
                          alt="Logo"
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 58,
                            height: 58,
                            objectFit: 'contain',
                          }}
                        />
                      )}
                    </div>
                    <div style={{
                      background: '#0F72B5',
                      color: '#fff',
                      padding: '10px 24px',
                      fontSize: 16,
                      fontWeight: 700,
                      textAlign: 'center',
                    }}>
                      {qrData.stationCode || qrData.label}
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: '#aaa', marginTop: 12, wordBreak: 'break-all', padding: '0 20px' }}>
                    {qrData.loginUrl}
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
                    <button
                      className="kiosk-btn-create"
                      onClick={handleDownloadQr}
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <DownloadIcon style={{ fontSize: 18 }} />
                      Download QR
                    </button>
                    <button
                      className="kiosk-btn-cancel"
                      onClick={() => {
                        const device = devices.find(d =>
                          qrData.label === d.label
                        );
                        if (device) handleRegenerateQr(device.id);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <RefreshIcon style={{ fontSize: 18 }} />
                      Regenerate
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskManagement;
